import { Link, usePage } from "@inertiajs/react"
import { ClipboardList, CreditCard, KeyRound, LogOut, Receipt, Settings, UserPen } from "lucide-react"
import { cn } from "@/lib/utils"
import { Separator } from "@/components/ui/separator"
import { t } from "@/lib/i18n"

export type ProfileNavItem = {
  title: string
  href: string
  icon: typeof UserPen
  testId: string
}

/** A run of related entries. Groups are separated by a hairline, nothing more. */
export type ProfileNavGroup = ProfileNavItem[]

const PROFILE_NAV_ITEMS: ProfileNavItem[] = [
  {
    title: t("ui.react.components.features.profiles.profile_sidebar.edit_profile"),
    href: "/profile/edit",
    icon: UserPen,
    testId: "profile-nav-edit",
  },
  {
    title: t("ui.react.components.features.profiles.profile_sidebar.change_password"),
    href: "/profile/password",
    icon: KeyRound,
    testId: "profile-nav-password",
  },
  {
    title: t("ui.react.components.features.profiles.profile_sidebar.configuration"),
    href: "/profile/settings",
    icon: Settings,
    testId: "profile-nav-settings",
  },
]

const PROFILE_NAV_GROUPS: ProfileNavGroup[] = [PROFILE_NAV_ITEMS]

/**
 * The diner hub, in two groups: what the diner did, then what the account is.
 *
 * Built at call time rather than frozen into a module constant, because
 * visibility depends on who is asking. A guest is a first-class diner here —
 * they have receipts and they have a language to set — but they have no account
 * screen and we never ask them for a card.
 *
 * `/dinein/orders` is offered **only while a visit is running**, and that is the
 * whole reason it is a parameter rather than a constant. It is the current visit
 * and nothing else — `User::VisitController` refuses it without one and sends the
 * diner to the dashboard — so a permanent entry here would be a link that bounces
 * for most of the people looking at this screen. The history across visits is
 * `/receipts`, whose rows are receipts rather than individual dishes.
 *
 * This is a holding position, not the final home for the route: it has had no
 * entry point since PR #288 removed it from the no-context nav, and the hub is
 * somewhere a diner already looks.
 */
export function dinerProfileGroups({
  anonymous,
  hasVisit = false,
}: {
  anonymous: boolean
  hasVisit?: boolean
}): ProfileNavGroup[] {
  const activity: ProfileNavItem[] = []

  if (hasVisit) {
    activity.push({
      title: t("ui.react.components.features.profiles.profile_sidebar.current_orders"),
      href: "/dinein/orders",
      icon: ClipboardList,
      testId: "profile-nav-orders",
    })
  }

  activity.push({
    title: t("ui.react.components.features.profiles.profile_sidebar.receipts"),
    href: "/receipts",
    icon: Receipt,
    testId: "profile-nav-receipts",
  })

  const account: ProfileNavItem[] = [
    {
      title: t("ui.react.components.features.profiles.profile_sidebar.configuration"),
      href: "/profile/settings",
      icon: Settings,
      testId: "profile-nav-settings",
    },
  ]

  if (!anonymous) {
    activity.push({
      title: t("ui.react.components.features.profiles.profile_sidebar.payment_methods"),
      href: "/profile/payment-methods",
      icon: CreditCard,
      testId: "profile-nav-payment-methods",
    })
    account.push({
      title: t("ui.react.components.features.profiles.profile_sidebar.account"),
      href: "/profile/edit",
      icon: UserPen,
      testId: "profile-nav-account",
    })
  }

  return [activity, account]
}

type ProfileSidebarProps = {
  groups?: ProfileNavGroup[]
}

export function ProfileSidebar({ groups = PROFILE_NAV_GROUPS }: ProfileSidebarProps) {
  const { url } = usePage()
  const populated = groups.filter((group) => group.length > 0)

  return (
    <nav className="flex flex-col gap-1">
      {populated.map((group, index) => (
        <div key={group[0].href} className="flex flex-col gap-1">
          {index > 0 && <Separator className="my-1" />}
          {group.map((item) => {
            const isActive = url === item.href

            return (
              <Link
                key={item.href}
                href={item.href}
                data-testid={item.testId}
                className={cn(
                  "flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors md:min-h-0",
                  isActive
                    ? "bg-muted text-foreground"
                    : "text-muted-foreground hover:bg-muted/50 hover:text-foreground"
                )}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                {item.title}
              </Link>
            )
          })}
        </div>
      ))}

      <Separator className="my-1" />

      <Link
        href="/session"
        method="delete"
        as="button"
        data-testid="profile-nav-sign-out"
        className="flex min-h-11 items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive md:min-h-0"
      >
        <LogOut className="h-4 w-4 shrink-0" />
        {t("ui.react.components.features.profiles.profile_sidebar.close_session")}
      </Link>
    </nav>
  )
}
